import { useEffect, useState } from 'react';
import { X, Tags } from 'lucide-react';
import toast from 'react-hot-toast';
import { useFeedStore } from '@/app/store/feedStore';
import { BUSINESS_SEGMENTS, BusinessSegment } from '@/shared/types/api';
import { getSegmentMeta } from '@/shared/lib/classificationMeta';
import { releaseTypeApi } from '@/shared/services/releaseTypeApi';
import { Spinner } from '@/shared/components/Spinner';
import { TagChip, TagSwatch } from '@/shared/components/TagChip';
import { cn } from '@/shared/utils/cn';

interface SegmentEditorModalProps {
  feedItemId: number;
  currentSegments: BusinessSegment[];
  /** Current tags on the item. Only editable by an admin (SC-240). */
  currentTags?: string[];
  /** Current Release Type value on the item (SC-300). */
  currentReleaseType?: string | null;
  isAdmin?: boolean;
  onClose: () => void;
}

const sameMembers = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && [...a].sort().every((v, i) => v === [...b].sort()[i]);

/**
 * "Update Metadata" (SC-240): business segments plus, for an admin, the item's tags. Renamed
 * from the segments-only editor and given a second panel in the same format, as the acceptance
 * criteria require. Tags are shared metadata rather than per-user state, so a non-admin sees
 * them read-only; the server rejects the write regardless.
 */
export function SegmentEditorModal({
  feedItemId,
  currentSegments,
  currentTags = [],
  currentReleaseType = null,
  isAdmin = false,
  onClose,
}: SegmentEditorModalProps) {
  const updateSegments = useFeedStore((state) => state.updateSegments);
  const updateTags = useFeedStore((state) => state.updateTags);
  const updateReleaseType = useFeedStore((state) => state.updateReleaseType);
  const availableTags = useFeedStore((state) => state.availableTags);
  const tagMeta = useFeedStore((state) => state.tagMeta);
  const tagsLoaded = useFeedStore((state) => state.tagsLoaded);
  const ensureTagOptions = useFeedStore((state) => state.ensureTagOptions);
  const [selected, setSelected] = useState<BusinessSegment[]>(currentSegments);
  const [selectedTags, setSelectedTags] = useState<string[]>(currentTags);
  const [releaseTypeOptions, setReleaseTypeOptions] = useState<string[]>([]);
  const [selectedReleaseType, setSelectedReleaseType] = useState<string>(currentReleaseType ?? '');
  const [saving, setSaving] = useState(false);

  // SC-300: offer the active Release Type vocabulary; keep the item's current value selectable
  // even if it was later deactivated, so opening the editor never silently drops it.
  useEffect(() => {
    let cancelled = false;
    releaseTypeApi
      .list()
      .then((types) => {
        if (cancelled) return;
        const names = types.map((t) => t.name);
        setReleaseTypeOptions(
          currentReleaseType && !names.includes(currentReleaseType)
            ? [...names, currentReleaseType]
            : names
        );
      })
      .catch(() => {
        if (!cancelled) setReleaseTypeOptions(currentReleaseType ? [currentReleaseType] : []);
      });
    return () => {
      cancelled = true;
    };
  }, [currentReleaseType]);

  useEffect(() => {
    void ensureTagOptions();
  }, [ensureTagOptions]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const toggleSegment = (segment: BusinessSegment) => {
    setSelected((prev) =>
      prev.includes(segment) ? prev.filter((s) => s !== segment) : [...prev, segment]
    );
  };

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  // Segments and tags are separate endpoints, so only the panels that actually changed are
  // written - saving must not stamp Manual over AI-suggested segments the user never touched.
  const handleSave = async () => {
    setSaving(true);
    try {
      const segmentsChanged = !sameMembers(selected, currentSegments);
      const tagsChanged = isAdmin && !sameMembers(selectedTags, currentTags);
      // Release Type is admin-managed shared metadata (like tags), so a non-admin's selection
      // is ignored on save; the server enforces this too.
      const releaseTypeChanged = isAdmin && selectedReleaseType !== (currentReleaseType ?? '');

      if (!segmentsChanged && !tagsChanged && !releaseTypeChanged) {
        onClose();
        return;
      }

      if (segmentsChanged) {
        const success = await updateSegments(feedItemId, selected);
        if (!success) {
          toast.error('Failed to update business segments');
          return;
        }
      }

      if (tagsChanged) {
        const success = await updateTags(feedItemId, selectedTags);
        if (!success) {
          toast.error('Failed to update tags');
          return;
        }
      }

      if (releaseTypeChanged) {
        const success = await updateReleaseType(feedItemId, selectedReleaseType || null);
        if (!success) {
          toast.error('Failed to update release type');
          return;
        }
      }

      toast.success('Metadata updated');
      onClose();
    } finally {
      setSaving(false);
    }
  };

  // SC-376: once the vocabulary has loaded, a tag the item carries that is no longer in it is a
  // removed (legacy) tag - shown read-only and kept on save, but never offered for selection
  // (decision 1). Before it loads, everything on the item stays offered so opening the popup
  // can never silently drop a tag.
  const legacyTags = tagsLoaded ? currentTags.filter((t) => !availableTags.includes(t)) : [];
  const tagOptions = tagsLoaded ? availableTags : Array.from(new Set([...availableTags, ...currentTags]));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-secondary-950/50 p-4 backdrop-blur-xs"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-lg animate-slide-up p-6 shadow-modal max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="mb-5 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <Tags className="h-5 w-5 text-primary" />
            Update Metadata
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="btn-icon btn-ghost btn-sm"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="label mb-2 block">Segments</label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {BUSINESS_SEGMENTS.map((segment) => {
                const meta = getSegmentMeta(segment);
                const isSelected = selected.includes(segment);
                return (
                  <button
                    key={segment}
                    type="button"
                    onClick={() => toggleSegment(segment)}
                    aria-pressed={isSelected}
                    title={meta.tooltip}
                    className={cn(
                      'rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                      isSelected
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-surface text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                  >
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            Saving marks these as confirmed and stops automatic AI suggestions for this item.
          </p>

          {/* Tags panel (SC-240) — same format as Segments, displayed below it */}
          <div>
            <label className="label mb-2 block">Tags</label>
            {tagOptions.length === 0 ? (
              <p className="text-sm text-muted-foreground">No tags available.</p>
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {tagOptions.map((tag) => {
                  const isSelected = selectedTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      disabled={!isAdmin}
                      aria-pressed={isSelected}
                      title={
                        isAdmin
                          ? tagMeta[tag]?.description || tag
                          : 'Only an admin can change tags'
                      }
                      className={cn(
                        'rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                        isSelected
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border bg-surface text-muted-foreground',
                        isAdmin
                          ? 'hover:bg-muted hover:text-foreground'
                          : 'cursor-not-allowed opacity-70'
                      )}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <TagSwatch color={tagMeta[tag]?.color} />
                        {tag}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {legacyTags.length > 0 && (
              <div className="mt-3" data-testid="legacy-tags">
                <p className="mb-1.5 text-xs text-muted-foreground">
                  Removed tags - kept on this item for history, but no longer assignable:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {legacyTags.map((tag) => (
                    <TagChip key={tag} name={tag} legacy />
                  ))}
                </div>
              </div>
            )}
            {!isAdmin && (
              <p className="mt-2 text-sm text-muted-foreground">
                Tags apply to this item for everyone, so only an admin can change them.
              </p>
            )}
          </div>

          {/* Release Type panel (SC-300) — a single-select, admin-managed metadata value */}
          <div>
            <label className="label mb-2 block" htmlFor="release-type-select">
              Release Type
            </label>
            <select
              id="release-type-select"
              value={selectedReleaseType}
              onChange={(e) => setSelectedReleaseType(e.target.value)}
              disabled={!isAdmin}
              className={cn('input w-full', !isAdmin && 'cursor-not-allowed opacity-70')}
            >
              <option value="">None</option>
              {releaseTypeOptions.map((rt) => (
                <option key={rt} value={rt}>
                  {rt}
                </option>
              ))}
            </select>
            {!isAdmin && (
              <p className="mt-2 text-sm text-muted-foreground">
                Release Type is shared metadata, so only an admin can change it.
              </p>
            )}
          </div>
          {selected.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No segments selected — saving will clear this item&apos;s segments and still stop
              AI suggestions.
            </p>
          )}

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="btn-primary flex-1"
            >
              {saving && <Spinner className="text-primary-foreground" />}
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
